/**
 * DashboardV2 — Cockpit V2.
 * Lecture Firestore V1 read-only via useV1Data.js. Aucune écriture.
 *
 * Données réelles : useTasks, useRequests, usePublications,
 *                   useObjectives, useCampagnes, useClubs, useCalendarItems
 */
import React, { useMemo, useState } from "react";
import {
  useTasks, useRequests, usePublications, useObjectives,
  useCampagnes, useClubs,
  filterTodayTasks, filterOpenRequests, filterPendingValidations, filterActiveObjectives,
} from "../hooks/useV1Data";
import { useCalendarItems } from "../hooks/useCalendarItems";
import { itemCoversDay } from "../lib/calendarItems";
import {
  IconCheckSquare, IconInbox, IconLayers,
  IconTarget, IconMegaphone,
} from "../icons";

/* ─────────────────────────────────────────────────────────
   HELPERS
   ───────────────────────────────────────────────────────── */

function greetingText(firstName) {
  const h = new Date().getHours();
  const n = firstName || "Mélissa";
  if (h < 12) return `Bonjour, ${n}`;
  if (h < 18) return `Bon après-midi, ${n}`;
  return `Bonsoir, ${n}`;
}

function formatDate() {
  const now = new Date();
  const s = now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function shortDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  const diff = Math.round((dt - Date.now()) / 86400000);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return "Demain";
  if (diff === -1) return "Hier";
  return dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

function pct(n) { return Math.min(100, Math.max(0, Number(n) || 0)); }

function statusVariant(s = "") {
  const v = s.toLowerCase();
  if (v.includes("urgent") || v.includes("haute")) return "red";
  if (v.includes("cours") || v.includes("actif")) return "blue";
  if (v.includes("valid") || v.includes("review")) return "yellow";
  if (v.includes("term") || v.includes("done") || v.includes("livr")) return "green";
  if (v.includes("reç") || v.includes("nouveau") || v.includes("ouverte")) return "orange";
  return "gray";
}

const OBJ_COLORS = ["yellow", "blue", "orange", "green"];

/** Construit un résolveur id→nom de club ; masque les ID techniques non résolus. */
function clubNameResolver(clubs) {
  const m = {};
  (clubs || []).forEach((c) => { m[String(c.id)] = c.name || c.nom || null; });
  return (v) => {
    if (v == null || v === "") return null;
    const s = String(v);
    if (m[s]) return m[s];
    return /^\d{6,}$/.test(s) ? null : s; // ID technique non résolu → masqué
  };
}

/* ─────────────────────────────────────────────────────────
   ATOMS
   ───────────────────────────────────────────────────────── */

function Badge({ variant = "gray", children }) {
  return <span className={`v2-badge v2-badge--${variant}`}>{children}</span>;
}

function Skeleton({ w = "100%", h = 12 }) {
  return <div className="v2-skel" style={{ width: w, height: h, borderRadius: 4, marginBottom: 6 }} />;
}

function SunIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="18" cy="18" r="8" fill="#FFB600"/>
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
        <line
          key={i}
          x1={18 + 11 * Math.cos(deg * Math.PI / 180)}
          y1={18 + 11 * Math.sin(deg * Math.PI / 180)}
          x2={18 + 14.5 * Math.cos(deg * Math.PI / 180)}
          y2={18 + 14.5 * Math.sin(deg * Math.PI / 180)}
          stroke="#FFB600"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M26 22.5A11 11 0 0 1 13.5 10a9 9 0 1 0 12.5 12.5Z" fill="#6b5bd4"/>
    </svg>
  );
}

function ChevLeft() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
function ChevRight() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}

function ObjIcon({ color }) {
  const icons = {
    yellow: <IconTarget size={18} />,
    blue:   <IconMegaphone size={18} />,
    orange: <IconCheckSquare size={18} />,
    green:  <IconLayers size={18} />,
  };
  return icons[color] || icons.blue;
}

/* ─────────────────────────────────────────────────────────
   COUNTER CARD
   ───────────────────────────────────────────────────────── */
function CounterCard({ icon: Icon, value, label, detail, color, onClick }) {
  return (
    <div
      className={`v2-counter v2-counter--${color}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
    >
      <div className="v2-counter__icon"><Icon size={20} /></div>
      <div className="v2-counter__body">
        <div className="v2-counter__head">
          <span className="v2-counter__value">{value}</span>
          <span className="v2-counter__label">{label}</span>
        </div>
        {detail && detail.length > 0 && (
          <div className="v2-counter__detail">
            {detail.map((d, i) => (
              <span key={i} className={`v2-chip v2-chip--${d.variant || "gray"}`}>{d.text}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   CAMPAIGN BANNER — dark visual
   ───────────────────────────────────────────────────────── */
function CampaignBanner({ campaign, onNavigate }) {
  if (!campaign) {
    return (
      <div className="v2-campaign-empty">
        <div className="v2-campaign-empty__icon"><IconMegaphone size={36} /></div>
        <div className="v2-campaign-empty__title">Aucune campagne active</div>
        <div className="v2-campaign-empty__sub">Les campagnes en cours s'afficheront ici.</div>
        <button
          className="v2-btn v2-btn--primary v2-btn--sm"
          style={{ marginTop: 8 }}
          onClick={() => onNavigate("campagnes")}
        >
          Voir les campagnes
        </button>
      </div>
    );
  }

  const hasProgress = campaign.progress != null || campaign.progression != null;
  const p = pct(campaign.progress != null ? campaign.progress : campaign.progression);
  const clubs = campaign.clubs || campaign.clubsIds || [];
  const dead = shortDate(campaign.deadline || campaign.dateEcheance || campaign.endDate || campaign.dateEnd);

  return (
    <div
      className="v2-campaign-banner"
      onClick={() => onNavigate("campagnes")}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onNavigate("campagnes")}
      aria-label={`Campagne : ${campaign.title || "—"}`}
    >
      <div className="v2-campaign-banner__left">
        <div className="v2-campaign-banner__eyebrow">
          <span className="v2-campaign-banner__dot" />
          Campagne en cours
          {campaign.isMock && (
            <span style={{ background: "rgba(255,255,255,.10)", borderRadius: 20, padding: "1px 7px", fontSize: 9, color: "rgba(255,255,255,.45)", marginLeft: 6, fontStyle: "normal", fontWeight: 500 }}>
              Aperçu
            </span>
          )}
        </div>

        <div className="v2-campaign-banner__title">
          {campaign.title || campaign.name || "—"}
        </div>

        {clubs.length > 0 && (
          <div className="v2-campaign-banner__clubs">
            {clubs.slice(0, 3).map((c, i) => (
              <span key={i} className="v2-campaign-banner__club">{c}</span>
            ))}
          </div>
        )}

        <div className="v2-campaign-banner__progress-area">
          {hasProgress && (
            <div className="v2-campaign-banner__progress-bar">
              <div className="v2-campaign-banner__progress-fill" style={{ width: `${p}%` }} />
            </div>
          )}
          <span className="v2-campaign-banner__pct">
            {hasProgress ? `${p} % complétée` : "Campagne active"}{dead ? ` · Échéance ${dead}` : ""}
          </span>
        </div>
      </div>

      <div className="v2-campaign-banner__right">
        <div className="v2-campaign-banner__quote">
          Plus<br />de padel<br />ensemble.
        </div>
        <button
          className="v2-campaign-banner__cta"
          onClick={(e) => { e.stopPropagation(); onNavigate("campagnes"); }}
          aria-label="Ouvrir la campagne"
        >
          <ChevRight />
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   MES TÂCHES À FAIRE — vraies tâches (ep:tasks), triées par échéance
   ───────────────────────────────────────────────────────── */
const TASK_CLOSED = new Set(["clôturée", "cloturee", "closed", "done", "completed", "terminé", "terminée", "termine", "terminee", "archivé", "archivee", "annulée", "annulee"]);

function taskPriority(t) {
  const p = String(t.priority || t.priorite || t.importance || "").toLowerCase();
  if (p.includes("haut") || p.includes("urgent") || p.includes("high") || p.includes("élev") || p.includes("elev")) return { variant: "red", label: "Haute" };
  if (p.includes("moy") || p.includes("medium") || p.includes("normal")) return { variant: "yellow", label: "Moyenne" };
  if (p.includes("bas") || p.includes("low") || p.includes("faible")) return { variant: "gray", label: "Basse" };
  return null;
}

function TasksCard({ tasks, loading, onNavigate }) {
  const { clubs } = useClubs();
  const clubNameOf = useMemo(() => clubNameResolver(clubs), [clubs]);

  const items = useMemo(() => {
    const list = (tasks || []).filter((t) =>
      t && t.done !== true && !TASK_CLOSED.has(String(t.status || t.statut || "").toLowerCase())
    );
    list.sort((a, b) => {
      const da = a.dueDate || a.deadline || a.date;
      const db = b.dueDate || b.deadline || b.date;
      const ta = da ? new Date(da).getTime() : Infinity;
      const tb = db ? new Date(db).getTime() : Infinity;
      return ta - tb;
    });
    return list.slice(0, 5);
  }, [tasks]);

  return (
    <div className="v2-block">
      <div className="v2-block-header">
        <span className="v2-block-header__label">Mes tâches à faire</span>
        {!loading && items.length > 0 && (
          <span className="v2-block-header__count">{items.length}</span>
        )}
        <span className="v2-block-header__link" onClick={() => onNavigate("projets")}>
          Voir tout <ChevRight />
        </span>
      </div>
      <div className="v2-block-divider" />

      {loading ? (
        <div style={{ padding: "14px 18px" }}>
          <Skeleton /><Skeleton w="80%" /><Skeleton w="60%" />
        </div>
      ) : items.length === 0 ? (
        <div className="v2-empty" style={{ padding: "28px 18px" }}>
          <div className="v2-empty__icon"><IconCheckSquare size={28} /></div>
          <div className="v2-empty__title">Aucune tâche en attente</div>
        </div>
      ) : (
        items.map((t, i) => {
          const due = shortDate(t.dueDate || t.deadline || t.date);
          const clubLabel = clubNameOf(t.club || (Array.isArray(t.clubs) && t.clubs[0]) || null);
          const prio = taskPriority(t);
          return (
            <div key={t.id || i} className="v2-recent-row" onClick={() => onNavigate("projets")}>
              <div className="v2-recent-row__type">
                <span className="v2-task-check" aria-hidden="true" />
              </div>
              <div className="v2-recent-row__body">
                <div className="v2-recent-row__title">{t.title || t.titre || t.name || t.label || "Tâche"}</div>
                <div className="v2-recent-row__meta">
                  {clubLabel && <><span>{clubLabel}</span><div className="v2-recent-row__meta-dot" /></>}
                  {due && <span>{due}</span>}
                </div>
              </div>
              <div className="v2-recent-row__right">
                {prio && <Badge variant={prio.variant}>{prio.label}</Badge>}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   OBJECTIVES CARD — icon circle style
   ───────────────────────────────────────────────────────── */
function ObjectivesCard({ objectives, loading, onNavigate }) {
  return (
    <div className="v2-block">
      <div className="v2-block-header">
        <span className="v2-block-header__label">Objectifs du mois</span>
        <span className="v2-block-header__link" onClick={() => onNavigate("objectifs")}>
          Voir tous <ChevRight />
        </span>
      </div>
      <div className="v2-block-divider" />

      {loading ? (
        <div style={{ padding: "14px 18px" }}>
          <Skeleton /><Skeleton w="75%" /><Skeleton w="55%" />
        </div>
      ) : objectives.length === 0 ? (
        <div className="v2-empty" style={{ padding: "28px 18px" }}>
          <div className="v2-empty__icon"><IconTarget size={28} /></div>
          <div className="v2-empty__title">Aucun objectif actif</div>
        </div>
      ) : (
        objectives.slice(0, 3).map((obj, i) => {
          const color = OBJ_COLORS[i % OBJ_COLORS.length];
          const p = pct(obj.progress || obj.progression);
          const current = obj.current ?? obj.valeurActuelle ?? null;
          const target  = obj.target  ?? obj.valeurCible   ?? null;
          const countLabel = (current !== null && target !== null)
            ? `${current} / ${target}`
            : `${p} %`;

          return (
            <div key={obj.id || i} className="v2-obj-item" onClick={() => onNavigate("objectifs")}>
              <div className={`v2-obj-item__icon v2-obj-item__icon--${color}`}>
                <ObjIcon color={color} />
              </div>
              <div className="v2-obj-item__body">
                <div className="v2-obj-item__top">
                  <span className="v2-obj-item__name">{obj.title || obj.titre || obj.name || "Objectif"}</span>
                  <span className="v2-obj-item__count">{countLabel}</span>
                </div>
                <div className={`v2-obj-progress v2-obj-progress--${color}`}>
                  <div className="v2-obj-progress__bar" style={{ width: `${p}%` }} />
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   AGENDA CARD — full right column
   ───────────────────────────────────────────────────────── */
function AgendaCard({ items, loading, onNavigate }) {
  const today = new Date();

  // Mois actuellement affiché (navigation ◀ ▶) + jour sélectionné (défaut : aujourd'hui).
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [selected, setSelected] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });

  const { year, month } = view;

  const monthName = new Date(year, month, 1)
    .toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const startOffset = (firstDayOfMonth + 6) % 7;

  const selStart = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());

  function goMonth(delta) {
    setView((v) => {
      const d = new Date(v.year, v.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  }
  function selectDay(day) {
    setSelected(new Date(year, month, day));
  }

  // Items couvrant le jour sélectionné — gère le multi-jours / « en cours » via itemCoversDay.
  const dayItems = useMemo(() => {
    const s = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());
    const list = (items || []).filter((it) => itemCoversDay(it, s));
    return list.sort((a, b) => {
      const at = a.allDay ? "" : (a.time || "99:99");
      const bt = b.allDay ? "" : (b.time || "99:99");
      return String(at).localeCompare(String(bt));
    });
  }, [items, selected]);

  // Événements à venir (après le jour sélectionné, 21 prochains jours), groupés par jour.
  const upcomingRows = useMemo(() => {
    const s = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());
    const list = (items || []).filter((it) => {
      if (!it.date) return false;
      const ds = new Date(it.date); ds.setHours(0, 0, 0, 0);
      const diff = Math.round((ds - s) / 86400000);
      return diff >= 1 && diff <= 21;
    }).sort((a, b) => {
      const da = new Date(a.date) - new Date(b.date);
      if (da !== 0) return da;
      return String(a.time || "99:99").localeCompare(String(b.time || "99:99"));
    }).slice(0, 6);
    let prev = null;
    return list.map((it) => {
      const d = new Date(it.date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const diff = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - s) / 86400000);
      let label = null;
      if (key !== prev) {
        prev = key;
        label = diff === 1 ? "Demain" : d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
        label = label.charAt(0).toUpperCase() + label.slice(1);
      }
      return { it, label };
    });
  }, [items, selected]);

  // Dots du mini-calendrier : couleurs des sources couvrant chaque jour du mois affiché.
  const dotsByDay = useMemo(() => {
    const map = {};
    for (let d = 1; d <= daysInMonth; d++) {
      const dd = new Date(year, month, d);
      const covering = (items || []).filter((it) => itemCoversDay(it, dd));
      if (covering.length > 0) {
        map[d] = covering.slice(0, 3).map((it) => (it.color && (it.color.border || it.color.text)) || "#0071CD");
      }
    }
    return map;
  }, [items, year, month, daysInMonth]);

  const cells = [];
  for (let i = 0; i < startOffset; i++) {
    cells.push({ day: daysInPrev - startOffset + 1 + i, other: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const isToday = year === today.getFullYear() && month === today.getMonth() && d === today.getDate();
    const isSelected = year === selected.getFullYear() && month === selected.getMonth() && d === selected.getDate();
    cells.push({ day: d, isToday, isSelected });
  }
  const rem = (7 - (cells.length % 7)) % 7;
  for (let d = 1; d <= rem; d++) cells.push({ day: d, other: true });

  const DOW = ["L", "M", "Me", "J", "V", "S", "D"];

  const isSelToday = selStart.getTime() === new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const selLabel = selStart.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const dayLabel = (isSelToday ? "Aujourd'hui · " : "") + selLabel.charAt(0).toUpperCase() + selLabel.slice(1);

  function slotLabel(it) {
    const started = it.endDate && new Date(it.date) < selStart;
    if (started) return "En cours";
    if (it.allDay) return "Journée";
    return it.time || "—";
  }

  function eventRow(it, slot, key) {
    const club = it.clubs && it.clubs[0] && it.clubs[0].name;
    return (
      <div
        key={key}
        className="v2-agenda-event"
        onClick={() => onNavigate("calendrier")}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onNavigate("calendrier")}
      >
        <span className="v2-agenda-event__time">{slot}</span>
        <div className="v2-agenda-event__dot" style={{ background: (it.color && (it.color.border || it.color.text)) || "#0071CD" }} />
        <div className="v2-agenda-event__body">
          <div className="v2-agenda-event__name">{it.title || "Événement"}</div>
          {club && <div className="v2-agenda-event__sub">{club}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="v2-block v2-agenda-block">
      <div className="v2-block-header">
        <span className="v2-block-header__label">Mon agenda</span>
        <span className="v2-block-header__link" onClick={() => onNavigate("calendrier")}>
          Voir tout <ChevRight />
        </span>
      </div>
      <div className="v2-block-divider" />

      <div className="v2-agenda-body">
        {/* Mini calendar */}
        <div className="v2-minical">
          <div className="v2-minical__header">
            <span className="v2-minical__month">{monthName}</span>
            <div className="v2-minical__nav">
              <button type="button" className="v2-minical__nav-btn" aria-label="Mois précédent" onClick={() => goMonth(-1)}><ChevLeft /></button>
              <button type="button" className="v2-minical__nav-btn" aria-label="Mois suivant" onClick={() => goMonth(1)}><ChevRight /></button>
            </div>
          </div>
          <div className="v2-minical__grid">
            {DOW.map((d, i) => <div key={i} className="v2-minical__dow">{d}</div>)}
            {cells.map((c, i) => {
              const dots = !c.other ? (dotsByDay[c.day] || []) : [];
              return (
                <div
                  key={i}
                  className={[
                    "v2-minical__day",
                    c.isToday ? "v2-minical__day--today" : "",
                    c.isSelected ? "v2-minical__day--selected" : "",
                    c.other   ? "v2-minical__day--other" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={c.other ? undefined : () => selectDay(c.day)}
                  role={c.other ? undefined : "button"}
                  tabIndex={c.other ? undefined : 0}
                  onKeyDown={c.other ? undefined : (e) => e.key === "Enter" && selectDay(c.day)}
                  style={{
                    cursor: c.other ? "default" : "pointer",
                    ...(c.isSelected && !c.isToday
                      ? { outline: "2px solid var(--blue, #0071CD)", outlineOffset: "-2px", borderRadius: 8 }
                      : null),
                  }}
                >
                  {c.day}
                  {dots.length > 0 && (
                    <div className="v2-minical__events">
                      {dots.map((col, j) => (
                        <div key={j} className="v2-minical__dot" style={{ background: col }} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Événements : aujourd'hui + à venir */}
        <div className="v2-agenda-events">
          <div className="v2-agenda-date-label">
            {dayLabel}
          </div>
          {loading ? (
            <div><Skeleton /><Skeleton w="70%" /></div>
          ) : dayItems.length === 0 ? (
            <div className="v2-agenda-empty">Aucun événement ce jour-là</div>
          ) : (
            dayItems.slice(0, 4).map((it, i) => eventRow(it, slotLabel(it), it.id || `t${i}`))
          )}

          {!loading && upcomingRows.map(({ it, label }, i) => (
            <React.Fragment key={it.id || `u${i}`}>
              {label && <div className="v2-agenda-date-label v2-agenda-date-label--up">{label}</div>}
              {eventRow(it, it.allDay ? "Journée" : (it.time || "—"), null)}
            </React.Fragment>
          ))}

          <button className="v2-agenda-add-btn" onClick={() => onNavigate("calendrier")}>
            + Ajouter un événement
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   DEMANDES & TÂCHES RÉCENTES
   ───────────────────────────────────────────────────────── */
function RecentDemandsTasks({ tasks, requests, loadingTasks, loadingReq, onNavigate }) {
  const { clubs } = useClubs();
  const clubNameOf = useMemo(() => clubNameResolver(clubs), [clubs]);
  const combined = useMemo(() => {
    const items = [];
    const closed = new Set(["clôturée", "closed", "refusée", "refused", "rejected", "done", "completed"]);

    (requests || []).forEach((r) => {
      if (!r || closed.has((r.status || "").toLowerCase())) return;
      items.push({
        type: "demande", id: r.id,
        title: r.title || r.titre || r.name || "Demande",
        club: r.club || (Array.isArray(r.clubs) && r.clubs[0]) || null, assignedTo: r.assignedTo || r.responsable,
        status: r.status || r.statut || "Reçue",
        date: r.createdAt || r.date || r.updatedAt,
      });
    });

    (tasks || []).forEach((t) => {
      if (!t || closed.has((t.status || "").toLowerCase()) || t.done === true) return;
      items.push({
        type: "tache", id: t.id,
        title: t.title || t.titre || t.name || "Tâche",
        club: t.club || (Array.isArray(t.clubs) && t.clubs[0]) || null, assignedTo: t.assignedTo || t.assignee,
        status: t.status || t.statut || "À faire",
        date: t.createdAt || t.dueDate || t.deadline,
      });
    });

    items.sort((a, b) => {
      const da = a.date ? new Date(a.date) : new Date(0);
      const db = b.date ? new Date(b.date) : new Date(0);
      return db - da;
    });
    return items.slice(0, 5);
  }, [tasks, requests]);

  const loading = loadingTasks || loadingReq;

  return (
    <div className="v2-block">
      <div className="v2-block-header">
        <span className="v2-block-header__label">Demandes clubs & tâches récentes</span>
        {!loading && combined.length > 0 && (
          <span className="v2-block-header__count">{combined.length}</span>
        )}
        <span className="v2-block-header__link" onClick={() => onNavigate("demandes")}>
          Voir tout <ChevRight />
        </span>
      </div>
      <div className="v2-block-divider" />

      {loading ? (
        <div style={{ padding: "14px 18px" }}>
          <Skeleton /><Skeleton w="80%" /><Skeleton w="65%" />
        </div>
      ) : combined.length === 0 ? (
        <div className="v2-empty" style={{ padding: "28px 18px" }}>
          <div className="v2-empty__icon"><IconInbox size={28} /></div>
          <div className="v2-empty__title">Aucun élément récent</div>
        </div>
      ) : (
        combined.map((item, i) => (
          <div
            key={item.id || i}
            className="v2-recent-row"
            onClick={() => onNavigate(item.type === "demande" ? "demandes" : "projets")}
          >
            <div className="v2-recent-row__type">
              {item.type === "demande"
                ? <Badge variant="orange">Demande</Badge>
                : <Badge variant="blue">Tâche</Badge>}
            </div>
            <div className="v2-recent-row__body">
              <div className="v2-recent-row__title">{item.title}</div>
              <div className="v2-recent-row__meta">
                {clubNameOf(item.club) && <><span>{clubNameOf(item.club)}</span><div className="v2-recent-row__meta-dot" /></>}
                {item.assignedTo && typeof item.assignedTo === "string" && (
                  <><span>{item.assignedTo}</span><div className="v2-recent-row__meta-dot" /></>
                )}
                {item.date && <span>{shortDate(item.date)}</span>}
              </div>
            </div>
            <div className="v2-recent-row__right">
              <Badge variant={statusVariant(item.status)}>{item.status}</Badge>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   DASHBOARD V2 — ROOT
   ───────────────────────────────────────────────────────── */
export default function DashboardV2({ appId, currentUser, onNavigate }) {
  const { tasks,        loading: loadingTasks } = useTasks();
  const { requests,     loading: loadingReq   } = useRequests();
  const { publications, loading: loadingPub   } = usePublications();
  const { objectives,   loading: loadingObj   } = useObjectives();
  const { campagnes,    loading: loadingCamp  } = useCampagnes();
  const { clubs: allClubs }                     = useClubs();
  const { items: calItems, loading: loadingCal } = useCalendarItems(currentUser, appId);

  const todayTasks         = useMemo(() => filterTodayTasks(tasks, appId),        [tasks, appId]);
  const openTasks          = useMemo(() => (tasks || []).filter((t) => t && t.done !== true && !TASK_CLOSED.has(String(t.status || t.statut || "").toLowerCase())), [tasks]);
  const openRequests       = useMemo(() => filterOpenRequests(requests),           [requests]);
  const pendingValidations = useMemo(() => filterPendingValidations(publications), [publications]);
  const activeObjectives   = useMemo(() => filterActiveObjectives(objectives),     [objectives]);

  // Détails des tuiles compteurs.
  const todayStart0 = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const overdueTasks = useMemo(() => openTasks.filter((t) => {
    const raw = t.dueDate || t.deadline || t.date; if (!raw) return false;
    const d = new Date(raw); return !isNaN(d.getTime()) && new Date(d.getFullYear(), d.getMonth(), d.getDate()) < todayStart0;
  }), [openTasks, todayStart0]);
  const weekTasks = useMemo(() => openTasks.filter((t) => {
    const raw = t.dueDate || t.deadline || t.date; if (!raw) return false;
    const d = new Date(raw); if (isNaN(d.getTime())) return false;
    const diff = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - todayStart0) / 86400000);
    return diff >= 0 && diff <= 7;
  }), [openTasks, todayStart0]);
  const reqToAttribute = useMemo(() => openRequests.filter((r) => {
    const assigned = (Array.isArray(r.assignedTo) && r.assignedTo.length > 0) || r.assigneeId != null || r.responsable;
    return !assigned;
  }), [openRequests]);

  // Campagne mise en avant : la campagne publiée active (début ≤ aujourd'hui ≤ fin),
  // sinon en cours sans fin, sinon la prochaine à venir, sinon la plus récente.
  const activeCampaign = useMemo(() => {
    if (loadingCamp) return null;
    const list = (campagnes || []).filter((c) => c && c.published);
    if (list.length === 0) return null;
    const today = new Date().toISOString().slice(0, 10);
    // Active aujourd'hui, sinon en cours sans date de fin, sinon la prochaine à venir.
    // Aucune de ces trois → on n'affiche pas une campagne terminée (état vide).
    const pick =
      list.find((c) => c.dateStart && c.dateEnd && c.dateStart <= today && today <= c.dateEnd) ||
      list.find((c) => c.dateStart && c.dateStart <= today && !c.dateEnd) ||
      list.find((c) => c.dateStart && today < c.dateStart) ||
      null;
    if (!pick) return null;

    // Progression temporelle (début → fin), si les deux dates existent.
    let progress = null;
    const s = pick.dateStart ? new Date(pick.dateStart).getTime() : null;
    const e = pick.dateEnd ? new Date(pick.dateEnd).getTime() : null;
    if (s != null && e != null && e > s) {
      progress = Math.round(((Date.now() - s) / (e - s)) * 100);
    }

    // Clubs → noms lisibles.
    const nameOf = {};
    (allClubs || []).forEach((c) => { nameOf[String(c.id)] = c.name || c.nom; });
    const rawClubs = Array.isArray(pick.clubs) ? pick.clubs : (pick.club != null ? [pick.club] : []);
    const clubNames = rawClubs.map((id) => nameOf[String(id)]).filter(Boolean);

    return {
      title: pick.name || pick.title || "Campagne",
      clubs: clubNames,
      progress,
      deadline: pick.dateEnd || null,
      isMock: false,
    };
  }, [campagnes, loadingCamp, allClubs]);

  const firstName = (currentUser?.name || "Mélissa Le Guen").split(" ")[0];

  return (
    <div className="v2-dashboard">

      {/* ── HERO ── */}
      <div className="v2-dash-hero">
        <div className="v2-dash-hero__left">
          <div className="v2-dash-hero__greeting">
            {greetingText(firstName)}
            <span className="v2-dash-hero__sun">
              {new Date().getHours() < 18 ? <SunIcon /> : <MoonIcon />}
            </span>
          </div>
          <div className="v2-dash-hero__sub">Voici ce qui vous attend aujourd'hui.</div>
        </div>
        <div className="v2-dash-hero__right">
          <div className="v2-dash-hero__date-label">{formatDate()}</div>
          <div className="v2-dash-hero__quote">
            "Des idées bien communiquées font grandir le padel."
            <span className="v2-dash-hero__quote-attr">— Esprit Padel</span>
          </div>
        </div>
      </div>

      {/* ── CORPS : pile unique, agenda en bas ── */}
      <div className="v2-dash-content">

        {/* Compteurs (compacts + détail) */}
        <div className="v2-dash-counters">
          <CounterCard
            icon={IconCheckSquare}
            value={loadingTasks ? "—" : todayTasks.length}
            label="À traiter aujourd'hui"
            detail={loadingTasks ? [] : (todayTasks.length > 0
              ? [{ text: `${todayTasks.length} à faire`, variant: "orange" }]
              : [{ text: "Aucune urgence", variant: "gray" }])}
            color="yellow"
            onClick={() => onNavigate("projets")}
          />
          <CounterCard
            icon={IconCheckSquare}
            value={loadingTasks ? "—" : openTasks.length}
            label="Mes tâches à faire"
            detail={loadingTasks ? [] : [
              ...(overdueTasks.length > 0 ? [{ text: `${overdueTasks.length} en retard`, variant: "red" }] : []),
              ...(weekTasks.length > 0 ? [{ text: `${weekTasks.length} cette semaine`, variant: "orange" }] : []),
              ...(overdueTasks.length === 0 && weekTasks.length === 0 ? [{ text: "À jour", variant: "green" }] : []),
            ]}
            color="blue"
            onClick={() => onNavigate("projets")}
          />
          <CounterCard
            icon={IconInbox}
            value={loadingReq ? "—" : openRequests.length}
            label="Demandes clubs"
            detail={loadingReq ? [] : (openRequests.length === 0
              ? [{ text: "Aucune demande", variant: "gray" }]
              : (reqToAttribute.length > 0
                ? [{ text: `${reqToAttribute.length} à attribuer`, variant: "orange" }]
                : [{ text: "Toutes attribuées", variant: "green" }]))}
            color="orange"
            onClick={() => onNavigate("demandes")}
          />
          <CounterCard
            icon={IconLayers}
            value={loadingPub ? "—" : pendingValidations.length}
            label="Validations"
            detail={loadingPub ? [] : (pendingValidations.length > 0
              ? [{ text: `${pendingValidations.length} à valider`, variant: "orange" }]
              : [{ text: "À jour", variant: "green" }])}
            color="gray"
            onClick={() => onNavigate("contenus")}
          />
        </div>

        <CampaignBanner campaign={activeCampaign} onNavigate={onNavigate} />

        {/* Mes tâches + Objectifs */}
        <div className="v2-dash-row">
          <TasksCard tasks={tasks} loading={loadingTasks} onNavigate={onNavigate} />
          <ObjectivesCard
            objectives={activeObjectives}
            loading={loadingObj}
            onNavigate={onNavigate}
          />
        </div>

        <RecentDemandsTasks
          tasks={tasks}
          requests={requests}
          loadingTasks={loadingTasks}
          loadingReq={loadingReq}
          onNavigate={onNavigate}
        />

        {/* Agenda en bas, pleine largeur */}
        <AgendaCard
          items={calItems}
          loading={loadingCal}
          onNavigate={onNavigate}
        />
      </div>
    </div>
  );
}
